import Campaign from "../../entities/campaign";

/**
 * Campaign actions
 *
 * Call order:
 *
 * CAMPAIGN_SAVE_QUEUE:	adds the item to the save queue
 * --> CAMPAIGN_SAVE:	modifies the state
 * --> CAMPAIGN_SAVE_SUCCESS: if all went well
 * --> CAMPAIGN_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail CAMPAIGN_SAVE_QUEUE is during the call to CAMPAIGN_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at CAMPAIGN_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: CAMPAIGN_SAVE_SUCCESS, CAMPAIGN_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or CAMPAIGN_PENDING_CHECK (it left the queue).
 */

export const CAMPAIGN_FETCH_ONE_QUEUE: any = 'CAMPAIGN_FETCH_ONE_QUEUE';
export const CAMPAIGN_FETCH_SUCCESS: any = 'CAMPAIGN_FETCH_SUCCESS';
export const CAMPAIGN_FETCH_FAILURE: any = 'CAMPAIGN_FETCH_FAILURE';
export const CAMPAIGN_FETCH_ONE_SUCCESS: any = 'CAMPAIGN_FETCH_ONE_SUCCESS';
export const CAMPAIGN_FETCH_ONE_FAILURE: any = 'CAMPAIGN_FETCH_ONE_FAILURE';

export const CAMPAIGN_SAVE: any = 'CAMPAIGN_SAVE';
export const CAMPAIGN_SAVE_QUEUE: any = 'CAMPAIGN_SAVE_QUEUE';
export const CAMPAIGN_SAVE_SUCCESS: any = 'CAMPAIGN_SAVE_SUCCESS';
export const CAMPAIGN_SAVE_FAILURE: any = 'CAMPAIGN_SAVE_FAILURE';

export const CAMPAIGN_SAVE_MANY: any = 'CAMPAIGN_SAVE_MANY';
export const CAMPAIGN_SAVE_MANY_QUEUE: any = 'CAMPAIGN_SAVE_MANY_QUEUE';
export const CAMPAIGN_SAVE_MANY_SUCCESS: any = 'CAMPAIGN_SAVE_MANY_SUCCESS';
export const CAMPAIGN_SAVE_MANY_FAILURE: any = 'CAMPAIGN_SAVE_MANY_FAILURE';

export const CAMPAIGN_REMOVE: any = 'CAMPAIGN_REMOVE';
export const CAMPAIGN_REMOVE_QUEUE: any = 'CAMPAIGN_REMOVE_QUEUE';
export const CAMPAIGN_REMOVE_SUCCESS: any = 'CAMPAIGN_REMOVE_SUCCESS';
export const CAMPAIGN_REMOVE_FAILURE: any = 'CAMPAIGN_REMOVE_FAILURE';

export const CAMPAIGN_RESTORE: any = 'CAMPAIGN_RESTORE';
export const CAMPAIGN_RESTORE_QUEUE: any = 'CAMPAIGN_RESTORE_QUEUE';
export const CAMPAIGN_RESTORE_SUCCESS: any = 'CAMPAIGN_RESTORE_SUCCESS';
export const CAMPAIGN_RESTORE_FAILURE: any = 'CAMPAIGN_RESTORE_FAILURE';

export const CAMPAIGN_RESTORE_MANY: any = 'CAMPAIGN_RESTORE_MANY';
export const CAMPAIGN_REMOVE_MANY: any = 'CAMPAIGN_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const CAMPAIGN_SAVE_REVERT: any = 'CAMPAIGN_SAVE_REVERT';
export const CAMPAIGN_PENDING_CHECK: any = 'CAMPAIGN_PENDING_CHECK';

/* Fetch actions */
export const fetchCampaignSuccess = (campaigns: Campaign[], partial: boolean, lastFetch: number) => ({type: CAMPAIGN_FETCH_SUCCESS, campaigns, partial, lastFetch});
export const fetchCampaignFailure = (err: any) => {
		console.error('CampaignAction: fetchCampaignFailure', err);
		return {type: CAMPAIGN_FETCH_FAILURE, err};
};
export const fetchOneCampaignQueue = (campaignId: string, sentAddedOn?: number) => ({type: CAMPAIGN_FETCH_ONE_QUEUE, campaignId, sentAddedOn});
export const fetchOneCampaignSuccess = (campaign: Campaign | null, sentAddedOn?: number, campaignId: string = campaign?.id) => ({type: CAMPAIGN_FETCH_ONE_SUCCESS, campaignId, campaign, sentAddedOn});
export const fetchOneCampaignFailure = (id: string, err: any) => {
		console.error('CampaignAction: fetchOneCampaignFailure', err);
		return {type: CAMPAIGN_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveCampaign = (campaign: Campaign) => {
    // console.debug('⮂ CAMPAIGN_SAVE action', campaign?.id);
    return {type: CAMPAIGN_SAVE, campaign};
}
export const saveCampaignQueue = (campaign: Campaign, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CAMPAIGN_SAVE_QUEUE action', campaign?.id);
    return {type: CAMPAIGN_SAVE_QUEUE, campaign, onQueued};
}
export const saveCampaignSuccess = (campaign: Campaign, sentAddedOn?: number) => {
    // console.debug('⮂ CAMPAIGN_SAVE_SUCCESS action', campaign?.id);
    return {type: CAMPAIGN_SAVE_SUCCESS, campaign, sentAddedOn};
}
export const saveCampaignFailure = (campaign: Campaign, status: number, message: string) => {
    console.warn(`⮂ CAMPAIGN_SAVE_FAILURE error saving campaign: HTTP ${status}: ${message}`, campaign?.id);
    return {type: CAMPAIGN_SAVE_FAILURE, campaign, status, message};
};

/* SaveMany actions */
export const saveManyCampaigns = (campaigns: Campaign[]) => {
    // console.debug('⮂ CAMPAIGN_SAVE_MANY action', campaigns?.map(campaign => campaign?.id).join(','));
    return {type: CAMPAIGN_SAVE_MANY, campaigns};
}
export const saveManyCampaignsQueue = (campaigns: Campaign[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CAMPAIGN_SAVE_MANY_QUEUE action', campaigns?.map(campaign => campaign.id).join(','));
    return {type: CAMPAIGN_SAVE_MANY_QUEUE, campaigns, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `campaigns` (OWA-1018) */
export const saveManyCampaignsSuccess = (campaigns: Campaign[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ CAMPAIGN_SAVE_MANY_SUCCESS action', campaigns?.map(campaign => campaign.id).join(','));
    return {type: CAMPAIGN_SAVE_MANY_SUCCESS, campaigns, sentAddedOns};
}
export const saveManyCampaignsFailure = (campaigns: Campaign[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_CAMPAIGNS_FAILURE Error saving campaigns: HTTP ${status}: ${message}`, campaigns?.map(campaign => campaign.id).join(','));
		return {type: CAMPAIGN_SAVE_MANY_FAILURE, campaigns, status, message};
};

/* Remove actions */
export const removeCampaign = (campaign: Campaign) => ({ type: CAMPAIGN_REMOVE, campaign });
export const removeCampaignQueue = (campaign: Campaign) => ({ type: CAMPAIGN_REMOVE_QUEUE, campaign });
export const removeCampaignSuccess = (campaign: Campaign) => ({ type: CAMPAIGN_REMOVE_SUCCESS, campaign });
export const removeCampaignFailure = (campaign: Campaign, status: number, message: string) => {
	console.error(`Redux: removeCampaignFailure Error removing campaign: HTTP ${status}: ${message}`);
	return ({ type: CAMPAIGN_REMOVE_FAILURE, campaign, status, message });
};

/* Restore actions */
export const restoreCampaign = (campaign: Campaign) => ({ type: CAMPAIGN_RESTORE, campaign });
export const restoreCampaignQueue = (campaign: Campaign) => ({ type: CAMPAIGN_RESTORE_QUEUE, campaign });
export const restoreCampaignSuccess = (campaign: Campaign, sentAddedOn?: number) => ({ type: CAMPAIGN_RESTORE_SUCCESS, campaign, sentAddedOn });
export const restoreCampaignFailure = (campaign: Campaign, status: number, message: string) => {
	console.error(`Redux: restoreCampaignFailure Error restoring campaign: HTTP ${status}: ${message}`);
	return ({ type: CAMPAIGN_RESTORE_FAILURE, campaign, status, message });
};

/* RestoreMany actions */
export const restoreManyCampaigns = (campaigns: Campaign[]) => ({ type: CAMPAIGN_RESTORE_MANY, campaigns });

/* RemoveMany actions */
export const removeManyCampaigns = (campaigns: Campaign[]) => ({ type: CAMPAIGN_REMOVE_MANY, campaigns });

/* Pending save actions (OWA-1018) */
/**
 * The save `campaign` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertCampaignSave = (campaign: Campaign, options: {sentAddedOn?: number, current?: Campaign | null, keep?: boolean} = {}) =>
    ({type: CAMPAIGN_SAVE_REVERT, campaign, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkCampaignPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: CAMPAIGN_PENDING_CHECK, live, refused, readAt});